import React, { useCallback, useEffect, useState } from "react";
import { AlertOctagon, Loader2, ChevronDown, ChevronRight, Wrench, Archive, ArrowRightLeft, CheckCircle2 } from "lucide-react";
import { api } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Badge } from "./ui/badge";
import { toast } from "sonner";

/**
 * Phase 101dn — Audit des lots orphelins (dans une machine automatique sans slot).
 *
 * Règle métier : tout lot posé dans un distributeur `type=AUTOMATIQUE` (hors
 * dépôt central `production_depot_id`) DOIT avoir un `emplacement_number`.
 * Les lots orphelins hérités des tests/exports antérieurs sont exposés ici
 * avec 3 actions manuelles :
 *   1. Attribuer un slot (fix normal)
 *   2. Rapatrier au Stock Conthey (retirer de la machine)
 *   3. Archiver le lot (soft-delete audit-safe pour les faux lots)
 */
export default function OrphanSlotsCard() {
  const [open, setOpen] = useState(true);
  const [items, setItems] = useState([]);
  const [count, setCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyLotId, setBusyLotId] = useState(null);
  const [slotDraft, setSlotDraft] = useState({}); // {lot_id: "12"}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.get("/lots/orphan-slots");
      setItems(r.data.items || []);
      setCount(r.data.count || 0);
    } catch (e) {
      toast.error("Impossible de charger les lots orphelins");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const fixSlot = async (lot) => {
    const slotRaw = slotDraft[lot.lot_id];
    const slot = parseInt(slotRaw, 10);
    if (!slot || Number.isNaN(slot)) {
      toast.error("Entrez un numéro de slot valide (ex : 12)");
      return;
    }
    setBusyLotId(lot.lot_id);
    try {
      await api.post(`/lots/orphan-slots/fix?lot_id=${encodeURIComponent(lot.lot_id)}&emplacement_number=${slot}`);
      toast.success(`Slot ${slot} attribué à ${lot.lot_number}`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'attribution du slot");
    } finally {
      setBusyLotId(null);
    }
  };

  const moveToCentral = async (lot) => {
    setBusyLotId(lot.lot_id);
    try {
      await api.post(`/lots/orphan-slots/move-to-central?lot_id=${encodeURIComponent(lot.lot_id)}`);
      toast.success(`Lot ${lot.lot_number} rapatrié au Stock Conthey`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors du rapatriement");
    } finally {
      setBusyLotId(null);
    }
  };

  const archive = async (lot) => {
    if (!window.confirm(
      `Archiver le lot ${lot.lot_number} (${lot.quantity}× ${lot.product_name}) ?\n\n` +
      `Il ne sera plus compté dans le stock actif mais restera consultable pour l'audit HACCP.`
    )) return;
    setBusyLotId(lot.lot_id);
    try {
      await api.post(`/lots/${encodeURIComponent(lot.lot_id)}/archive-orphan`);
      toast.success(`Lot ${lot.lot_number} archivé`);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur lors de l'archivage");
    } finally {
      setBusyLotId(null);
    }
  };

  if (loading) {
    return (
      <div className="border border-[#E5E5E0] rounded-lg p-4 flex items-center text-sm text-[#575A57]" data-testid="orphan-slots-loading">
        <Loader2 className="w-4 h-4 animate-spin mr-2" />
        Audit des lots sans emplacement…
      </div>
    );
  }

  if (count === 0) {
    return (
      <div className="border border-[#C5DBCF] bg-[#EAF4EE] rounded-lg p-3 flex items-center gap-2 text-sm text-[#1F4E3D]" data-testid="orphan-slots-clean">
        <CheckCircle2 className="w-4 h-4" />
        <span>Aucun lot orphelin — toutes les machines ont bien leurs positions attribuées.</span>
      </div>
    );
  }

  return (
    <div className="border border-[#E8D3A8] bg-[#FBF4E9] rounded-lg" data-testid="orphan-slots-card">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 p-3 text-left hover:bg-[#F5E7C7]/50 transition-colors rounded-lg"
        data-testid="orphan-slots-toggle"
      >
        <AlertOctagon className="w-5 h-5 text-[#8C6B2F] flex-shrink-0" />
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-[#5A3D1A]">
            Lots sans emplacement dans une machine automatique
          </div>
          <div className="text-xs text-[#8C6B2F] mt-0.5">
            {count} lot{count > 1 ? "s" : ""} orphelin{count > 1 ? "s" : ""} — attribuez un slot, rapatriez au stock central ou archivez si obsolète.
          </div>
        </div>
        <Badge className="bg-[#C05C47] text-white">{count}</Badge>
        {open ? <ChevronDown className="w-4 h-4 text-[#8C6B2F]" /> : <ChevronRight className="w-4 h-4 text-[#8C6B2F]" />}
      </button>

      {open && (
        <div className="border-t border-[#E8D3A8] p-3 space-y-2 bg-white/50">
          {items.map((lot) => (
            <div
              key={lot.lot_id}
              className="bg-white border border-[#E5E5E0] rounded-md p-3 flex items-start gap-3 flex-wrap"
              data-testid={`orphan-slot-row-${lot.lot_id}`}
            >
              <div className="flex-1 min-w-[240px]">
                <div className="text-sm font-medium text-[#1A1C1A]">
                  {lot.product_name} <span className="text-[#575A57] font-normal">· lot {lot.lot_number}</span>
                </div>
                <div className="text-xs text-[#575A57] mt-1">
                  <span className="font-medium">{lot.quantity}</span> unité{lot.quantity > 1 ? "s" : ""}
                  {" · "}
                  <span className="text-[#8C6B2F]">Machine : {lot.distributor_name}</span>
                  {lot.dlc && <>{" · "}DLC {String(lot.dlc).slice(0, 10)}</>}
                </div>
              </div>

              <div className="flex items-center gap-2 flex-wrap">
                <div className="flex items-center gap-1">
                  <Input
                    type="number"
                    placeholder="Slot"
                    className="h-8 w-20 text-sm"
                    value={slotDraft[lot.lot_id] || ""}
                    onChange={(e) => setSlotDraft((d) => ({ ...d, [lot.lot_id]: e.target.value }))}
                    data-testid={`orphan-slot-input-${lot.lot_id}`}
                  />
                  <Button
                    size="sm"
                    className="h-8 bg-[#1F4E3D] hover:bg-[#173B2E]"
                    disabled={busyLotId === lot.lot_id}
                    onClick={() => fixSlot(lot)}
                    data-testid={`orphan-slot-fix-${lot.lot_id}`}
                  >
                    <Wrench className="w-3.5 h-3.5 mr-1.5" /> Attribuer
                  </Button>
                </div>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8"
                  disabled={busyLotId === lot.lot_id}
                  onClick={() => moveToCentral(lot)}
                  data-testid={`orphan-slot-move-${lot.lot_id}`}
                  title="Retirer de la machine et rapatrier au Stock Conthey"
                >
                  <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" /> Vers Conthey
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="h-8 text-[#8C6B2F] hover:text-[#C05C47]"
                  disabled={busyLotId === lot.lot_id}
                  onClick={() => archive(lot)}
                  data-testid={`orphan-slot-archive-${lot.lot_id}`}
                  title="Archiver (soft-delete, traçabilité HACCP conservée)"
                >
                  <Archive className="w-3.5 h-3.5 mr-1.5" /> Archiver
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
